import { useState } from 'react'
import type { PlanningBlock, PublicPlanning } from '@publiqa/shared'
import { useFetch } from '../../lib/useFetch'
import { usePublic, usePubliqApi } from '../context'
import { SectionHead } from './Blocks'

const hm = (t: string) => t.slice(0, 5).replace(':00', 'h').replace(':', 'h')

/** Jour local AAAA-MM-JJ. */
function todayLocal(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function addDays(day: string, n: number): string {
  const d = new Date(`${day}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}
function monday(day: string): string {
  const wd = new Date(`${day}T12:00:00Z`).getUTCDay()
  return addDays(day, wd === 0 ? -6 : 1 - wd)
}
const fmt = (day: string, opts: Intl.DateTimeFormatOptions) =>
  new Date(`${day}T12:00:00`).toLocaleDateString('fr-FR', opts)
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

/**
 * Planning des cours : les séances de la semaine, jour par jour, avec un
 * filtre par groupe. Les annulations (et leur motif), les vacances et les
 * fermetures y sont signalées. Il suit le planning géré dans le back-office.
 */
export function PlanningView({ b }: { b: PlanningBlock }) {
  const api = usePubliqApi()
  const { inert } = usePublic()
  const [week, setWeek] = useState(() => monday(todayLocal()))
  const [groupId, setGroupId] = useState('')
  const end = addDays(week, 6)
  const { data } = useFetch<PublicPlanning>(api('/planning', { from: week, to: end }))
  const thisWeek = monday(todayLocal())
  const groups = data?.groups ?? []
  const byId = new Map(groups.map((g) => [g.id, g]))
  const sessions = (data?.sessions ?? [])
    .filter((s) => !groupId || s.groupId === groupId)
    .sort((a, b) => (a.day + a.startsAt).localeCompare(b.day + b.startsAt))
  const days = [...new Set(sessions.map((s) => s.day))]
  const closures = data?.closures ?? []
  const label =
    week.slice(0, 7) === end.slice(0, 7)
      ? `Du ${Number(week.slice(8))} au ${fmt(end, { day: 'numeric', month: 'long' })}`
      : `Du ${fmt(week, { day: 'numeric', month: 'long' })} au ${fmt(end, { day: 'numeric', month: 'long' })}`

  if (data && !data.season && !inert) return null
  return (
    <section className="b-section b-planning">
      <div className="container">
        <SectionHead title={b.title} intro={b.intro} />

        <div className="pl-bar">
          <div className="pl-week">
            <button
              type="button"
              className="pl-arrow"
              aria-label="Semaine précédente"
              onClick={() => setWeek(addDays(week, -7))}
            >
              ‹
            </button>
            <div className="pl-week-label" aria-live="polite">
              <small>{week === thisWeek ? 'Cette semaine' : 'Semaine'}</small>
              <strong>{label}</strong>
            </div>
            <button
              type="button"
              className="pl-arrow"
              aria-label="Semaine suivante"
              onClick={() => setWeek(addDays(week, 7))}
            >
              ›
            </button>
            {week !== thisWeek && (
              <button type="button" className="pl-today" onClick={() => setWeek(thisWeek)}>
                Cette semaine
              </button>
            )}
          </div>
          {groups.length > 1 && (
            <div className="pl-groups" role="tablist" aria-label="Groupe">
              <button
                type="button"
                role="tab"
                aria-selected={!groupId}
                className={!groupId ? 'on' : ''}
                onClick={() => setGroupId('')}
              >
                Tous les groupes
              </button>
              {groups.map((g) => (
                <button
                  type="button"
                  role="tab"
                  key={g.id}
                  aria-selected={g.id === groupId}
                  className={g.id === groupId ? 'on' : ''}
                  onClick={() => setGroupId(g.id)}
                >
                  <i style={{ background: g.color }} aria-hidden="true" /> {g.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {closures.length > 0 && (
          <ul className="pl-closures">
            {closures.map((c) => (
              <li key={`${c.label} ${c.startsOn}`}>
                <strong>{c.label}</strong> —{' '}
                {c.startsOn === c.endsOn
                  ? `pas d’entraînement le ${fmt(c.startsOn, { weekday: 'long', day: 'numeric', month: 'long' })}`
                  : `du ${fmt(c.startsOn, { day: 'numeric', month: 'long' })} au ${fmt(c.endsOn, { day: 'numeric', month: 'long' })}`}
              </li>
            ))}
          </ul>
        )}

        {!data ? (
          <div className="pl-loading" aria-busy="true" />
        ) : !days.length ? (
          <p className="pl-empty">Aucun cours cette semaine.</p>
        ) : (
          <div className="pl-days">
            {days.map((d) => (
              <div key={d} className={`pl-day${d === todayLocal() ? ' today' : ''}`}>
                <h3>
                  {cap(fmt(d, { weekday: 'long' }))}{' '}
                  <span>{fmt(d, { day: 'numeric', month: 'long' })}</span>
                </h3>
                <ul>
                  {sessions
                    .filter((s) => s.day === d)
                    .map((s) => {
                      const g = byId.get(s.groupId)
                      return (
                        <li
                          key={`${s.groupId} ${s.startsAt}`}
                          className={s.cancelled ? 'cancelled' : ''}
                          style={{ borderLeftColor: g?.color }}
                        >
                          <span className="pl-time">
                            {hm(s.startsAt)} – {hm(s.endsAt)}
                          </span>
                          <span className="pl-group">
                            <strong>{g?.name}</strong>
                            {s.location && <small>{s.location}</small>}
                          </span>
                          {s.cancelled ? (
                            <span className="pl-badge cancelled">
                              Annulé{s.reason ? ` · ${s.reason}` : ''}
                            </span>
                          ) : (
                            s.extra && <span className="pl-badge extra">Séance ajoutée</span>
                          )}
                        </li>
                      )
                    })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
